// packages/shared/src/phone.ts
//
// Phone E.164 normalization helper — used by Zod transforms at the route
// layer (POST /register, POST /resend-otp etc.) BEFORE Redis key composition
// in services/otp/otp.service.ts. Implements FIX-05 from PLAN Task 16:
//   - 0901234567 + defaultCountry='VN' → +84901234567
//   - +84901234567 → +84901234567 (idempotent)
//   - 84901234567 (raw country-coded) → +84901234567 (when length matches)
//   - Unparseable input → throws PhoneFormatError
//
// Why centralize here:
//   - All callers (auth/register, auth/resend-otp, OTP rate-limit key
//     composition) MUST agree on a single canonical form so the AC scenario
//     "0901234567 / +84901234567 / 84901234567 → same rl:otp:phone:+84901234567
//     bucket" holds. Centralizing the parser is the only way to guarantee
//     that — copy-pasting regex per caller is a known footgun.
//   - This package is intentionally node-pure (no Fastify / Mongoose /
//     React) so FE bundles can import it via Vite without pulling Node-only
//     deps. Per PLAN Task 39 + FIX-13 in CONTEXT.md.
//
// Phase 1 country scope: 'VN' is the canonical default (Printway is VN-first
// per PROJECT brief). Other ISO 3166-1 alpha-2 codes are accepted by
// libphonenumber-js as defaults (e.g. 'US', 'GB', 'JP', ...) so the helper is
// future-proof for cross-border expansion in later phases.
import { parsePhoneNumberFromString } from 'libphonenumber-js';
// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
/**
 * Thrown when `normalizePhoneE164` receives input that cannot be parsed into
 * a valid E.164 phone number. Callers (Zod transforms) should map this to
 * a 400 BAD_REQUEST response; do NOT leak the raw input back to the user.
 */
export class PhoneFormatError extends Error {
    input;
    defaultCountry;
    constructor(input, defaultCountry, reason) {
        super(`Invalid phone number — ${reason} (input length ${input.length}, defaultCountry ${defaultCountry})`);
        this.name = 'PhoneFormatError';
        this.input = input;
        this.defaultCountry = defaultCountry;
    }
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Strips internal whitespace and common formatting characters: spaces, dashes,
 * dots, and parentheses. Leading `+` is preserved. Internal letter chars are
 * stripped — we don't accept "letter dial codes" (e.g. "1-800-FLOWERS").
 */
function stripFormatting(raw) {
    return raw.replace(/[\s\-.()/]/g, '').trim();
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Normalizes a phone number string to E.164 format (`+CCNNNNNNNNN`).
 *
 * Behavior matrix (Phase 1 — VN-first):
 *   normalizePhoneE164('0901234567', 'VN')         → '+84901234567'
 *   normalizePhoneE164('+84901234567', 'VN')       → '+84901234567' (idempotent)
 *   normalizePhoneE164('+84 901-234-567', 'VN')    → '+84901234567' (formatted)
 *   normalizePhoneE164('84901234567', 'VN')        → '+84901234567' (raw country-coded)
 *   normalizePhoneE164('  +1 (415) 555-2671  ', 'VN') → '+14155552671'
 *   normalizePhoneE164('not-a-phone', 'VN')        → throws PhoneFormatError
 *   normalizePhoneE164('', 'VN')                   → throws PhoneFormatError
 *
 * Invariants:
 *   - Output ALWAYS starts with `+` followed by digits only.
 *   - Output is canonical: same logical number → same string regardless of
 *     formatting variations in input.
 *   - Idempotent: `normalizePhoneE164(normalizePhoneE164(x, 'VN'), 'VN') === normalizePhoneE164(x, 'VN')`
 *     for any valid input `x`.
 *
 * @param raw User-supplied phone string (may contain spaces / dashes / `+`).
 * @param defaultCountry ISO 3166-1 alpha-2 code (e.g. `'VN'`, `'US'`) used
 *                       when `raw` lacks a country code prefix.
 * @returns E.164-formatted phone number string.
 * @throws {PhoneFormatError} if `raw` cannot be parsed into a valid number.
 */
export function normalizePhoneE164(raw, defaultCountry) {
    if (typeof raw !== 'string' || raw.length === 0) {
        throw new PhoneFormatError(String(raw ?? ''), defaultCountry, 'empty input');
    }
    const cleaned = stripFormatting(raw);
    if (cleaned.length === 0) {
        throw new PhoneFormatError(raw, defaultCountry, 'empty after strip');
    }
    // Treat raw country-coded inputs (e.g. "84901234567") as E.164-without-plus
    // when they would not be a valid local number under defaultCountry. The
    // libphonenumber-js parser handles the explicit `+` prefix natively but
    // does not auto-prepend `+` to a digits-only string — so we try the
    // `+`-prefixed parse first when input has no leading `+` AND no leading 0.
    const candidates = [];
    if (cleaned.startsWith('+')) {
        candidates.push(cleaned);
    }
    else if (cleaned.startsWith('0')) {
        // Local number — needs defaultCountry to resolve.
        candidates.push(cleaned);
    }
    else {
        // Raw digits — try `+` prefix first (assume country-coded), then fall
        // back to local-with-defaultCountry.
        candidates.push(`+${cleaned}`);
        candidates.push(cleaned);
    }
    for (const candidate of candidates) {
        const parsed = parsePhoneNumberFromString(candidate, defaultCountry);
        if (parsed && parsed.isValid()) {
            return parsed.number; // libphonenumber-js .number returns E.164 string
        }
    }
    throw new PhoneFormatError(raw, defaultCountry, 'unparseable by libphonenumber');
}
/**
 * Best-effort country detection from an E.164 phone number. Returns the ISO
 * 3166-1 alpha-2 code (e.g. `'VN'`, `'US'`) or `undefined` if the number
 * could not be matched. Useful for tagging audit rows without a separate
 * round-trip to the route-layer geo header.
 *
 * Idempotent + side-effect-free.
 */
export function detectCountryFromE164(e164) {
    if (typeof e164 !== 'string' || !e164.startsWith('+'))
        return undefined;
    const parsed = parsePhoneNumberFromString(e164);
    return parsed?.country;
}
//# sourceMappingURL=phone.js.map